import type { BeanDraft, BeanPageExtractor } from "./beanInfo";
import { PROXY_URL } from "./proxyLoader";

/**
 * Layer 3: LLM extraction of variety / region / process / producer from the description text.
 *
 * Runs only when the earlier layers left at least one of those fields empty and a description exists. The call goes
 * to the Worker's POST /extract endpoint, which holds the Claude API key server-side; the phone never sees it. If the
 * Worker has no key configured (503) or anything fails, the layer returns null and the import proceeds without it.
 */
export interface ExtractRequest { description: string; name?: string; roaster?: string }
export interface ExtractResponse { variety: string | null; region: string | null; process: string | null; producer: string | null }

/** POST JSON and return the parsed body, or null on any failure. Injected so tests never touch the network. */
export type JsonPoster = (url: string, body: ExtractRequest) => Promise<ExtractResponse | null>;

export function fetchJsonPoster(timeoutMs = 30_000): JsonPoster {
  return async (url, body) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const res = await fetch(url, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body), signal: controller.signal,
      });
      if (!res.ok) return null;
      return (await res.json()) as ExtractResponse;
    } catch {
      return null;
    } finally {
      clearTimeout(timer);
    }
  };
}

export class LlmExtractor implements BeanPageExtractor {
  readonly name = "llm";

  constructor(
    private readonly post: JsonPoster = fetchJsonPoster(),
    private readonly endpoint: string = PROXY_URL ? `${PROXY_URL}/extract` : "",
  ) {}

  async extract(_url: string, _html: string, _loader: unknown, soFar?: BeanDraft | null): Promise<Partial<BeanDraft> | null> {
    if (!this.endpoint || !soFar?.description) return null;
    const missing = !soFar.variety || !soFar.region || !soFar.process || !soFar.producer;
    if (!missing) return null;

    const body: ExtractRequest = { description: soFar.description.slice(0, 8_000) };
    if (soFar.name) body.name = soFar.name;
    if (soFar.roaster) body.roaster = soFar.roaster;
    const r = await this.post(this.endpoint, body);
    if (!r) return null;
    return { variety: str(r.variety), region: str(r.region), process: str(r.process), producer: str(r.producer) };
  }
}

function str(v: unknown): string | null {
  return typeof v === "string" && v.trim() !== "" ? v.trim() : null;
}
